import { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { months, capacityMatrix, pendingApprovalsList } from "../lib/calc";
import { leaveByPersonMonth } from "../lib/leave";
import { ratesFromData, resolvedPeople } from "../lib/personnel";
import { projectProfitability, marginColor } from "../lib/profitability";
import { fmtMoney, fmtPct, fmtMonthShort } from "../lib/format";
import { parseLoad } from "./project/PersonnelAllocations";
import { toast } from "sonner";
import { Check, X, Pencil, CheckCheck, AlertTriangle, Plane } from "lucide-react";

// Management approval queue for proposed resourcing. Grouped by project.
// Every item shows cost impact, before/after capacity per affected month,
// and the project margin with the proposal included — decisions without
// leaving the queue.
export function ApprovalsQueue() {
  const { rawData, refresh } = useData();
  const m = months(rawData);
  const matrix = useMemo(() => capacityMatrix(rawData), [rawData]);
  const leaveFrac = useMemo(() => leaveByPersonMonth(rawData), [rawData]);
  const rates = useMemo(() => ratesFromData(rawData), [rawData]);
  const pending = useMemo(() => pendingApprovalsList(rawData), [rawData]);
  const people = useMemo(() => resolvedPeople(rawData), [rawData]);
  const [busyKey, setBusyKey] = useState(null);

  const personOf = (id) => people.find((p) => p.id === id);
  const tierOf = (id, alloc) => {
    if (alloc?.tbd) return alloc.tier || null;
    const person = personOf(id);
    const rec = (rawData?.personnel || []).find(
      (pp) => pp.fullName === person?.name || pp.id === id
    );
    return rec?.tier || person?.tier || null;
  };
  const rateFor = (tier) =>
    (tier && rates?.tiers?.[tier]?.count ? rates.tiers[tier].avgMonthly : rates?.firm?.avgMonthly) || 0;
  const principalName = (pid) =>
    (rawData?.principals || []).find((p) => p.id === pid)?.name || pid;

  const act = async (key, fn, okMsg) => {
    setBusyKey(key);
    try {
      await fn();
      await refresh();
      toast.success(okMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed — check your connection and try again");
    } finally {
      setBusyKey(null);
    }
  };

  const groups = useMemo(() => {
    const by = new Map();
    for (const { project, alloc } of pending) {
      if (!by.has(project.id)) by.set(project.id, { project, items: [] });
      by.get(project.id).items.push(alloc);
    }
    return [...by.values()];
  }, [pending]);

  if (!pending.length) {
    return (
      <div className="border border-[var(--rule)] py-16 text-center" data-testid="approvals-empty">
        <div className="eyebrow text-[var(--soft-fg)]">No resourcing awaiting approval</div>
        <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-2">
          When a principal proposes people on a project, the request lands here for sign-off.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approvals-queue">
      {groups.map(({ project, items }) => {
        const prof = projectProfitability(project, rates, people);
        return (
          <section key={project.id} className="border border-[var(--rule)]" data-testid={`approvals-project-${project.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[var(--rule)] bg-[var(--paper-soft)]">
              <div>
                <span className="font-display text-base">{project.name}</span>
                <span className="eyebrow ml-3">proposed by {principalName(project.principalId)}</span>
              </div>
              <button
                onClick={() => act(`bulk-${project.id}`, () =>
                  api.post("/approvals/approve-project", { projectId: project.id }),
                  `Approved all pending for ${project.name}`)}
                disabled={busyKey === `bulk-${project.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--ink)] text-white text-[11px] font-display tracked-tight uppercase disabled:opacity-40"
                data-testid={`approve-all-${project.id}`}
              >
                <CheckCheck size={12} /> Approve all ({items.length})
              </button>
            </div>
            {items.map((alloc) => (
              <QueueItem
                key={alloc.personId}
                alloc={alloc}
                project={project}
                m={m}
                matrix={matrix}
                leaveFrac={leaveFrac}
                people={people}
                person={personOf(alloc.personId)}
                tier={tierOf(alloc.personId, alloc)}
                monthlyRate={rateFor(tierOf(alloc.personId, alloc))}
                prof={prof}
                busyKey={busyKey}
                act={act}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

function QueueItem({ alloc, project, m, matrix, leaveFrac, people, person, tier, monthlyRate, prof, busyKey, act }) {
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState(() => ({ ...(alloc.proposedValues || {}) }));
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const key = `${project.id}-${alloc.personId}`;
  const isTbd = !!alloc.tbd;

  const proposed = editing ? vals : (alloc.proposedValues || {});
  const approved = alloc.approvedValues || {};

  // Cost impact: extra person-months × tier-average monthly rate.
  const deltaFte = m.reduce((s, mo) => s + ((proposed[mo] || 0) - (approved[mo] || 0)), 0);
  const costImpact = deltaFte * monthlyRate;

  // Full month-by-month capacity strip for the person under review (or the
  // prospective assignee for TBD rows): current approved %, proposed +%,
  // resulting % — red when the result exceeds available capacity after leave.
  const stripPid = isTbd ? (assignee || null) : alloc.personId;
  const windowMonths = m.filter((mo) => (proposed[mo] || 0) > 0 || (approved[mo] || 0) > 0);
  const strip = stripPid
    ? windowMonths.map((mo) => {
        const before = matrix[stripPid]?.[mo] || 0;
        const delta = (proposed[mo] || 0) - (approved[mo] || 0);
        const after = before + delta;
        const lv = leaveFrac[stripPid]?.[mo] || 0;
        // Leave is ADDITIVE — it stacks on top of load. Displayed % is the
        // total demand: (booked + leave). Over the line at total > 100%.
        const beforeDisp = before + lv;
        const afterDisp  = after  + lv;
        const over = afterDisp > 1 + 0.0001;
        return { mo, before, beforeDisp, delta, after, afterDisp, lv, over };
      })
    : [];

  // TBD suggestion: least-loaded active people in the tier over the window.
  const tierPeople = isTbd
    ? people
        .filter((p) => p.tier === tier)
        .map((p) => ({
          ...p,
          avgLoad: windowMonths.length
            ? windowMonths.reduce((s, mo) => s + (matrix[p.id]?.[mo] || 0), 0) / windowMonths.length
            : 0,
        }))
        .sort((a, b) => a.avgLoad - b.avgLoad)
    : [];

  // Project margin with this proposal included.
  const feeSide = prof.fee || 0;
  const costWith = (prof.cost || 0) + costImpact;
  const marginWith = feeSide > 0 ? (feeSide - costWith) / feeSide : null;

  const approve = () =>
    act(key, () => api.post("/approvals/approve", {
      projectId: project.id, personId: alloc.personId,
      ...(isTbd ? { assignPersonId: assignee } : {}),
      ...(editing ? { values: Object.fromEntries(Object.entries(vals).filter(([, v]) => v)) } : {}),
    }), `Approved ${isTbd ? (people.find((p) => p.id === assignee)?.name || "assignee") : (person?.name || alloc.personId)} on ${project.name}`);

  const reject = () =>
    act(key, () => api.post("/approvals/reject", {
      projectId: project.id, personId: alloc.personId, note,
    }), `Rejected proposal for ${person?.name || alloc.personId}`);

  return (
    <div className="px-4 py-4 border-b border-[var(--rule)] last:border-b-0" data-testid={`approval-item-${key}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[180px]">
          <div className="text-sm text-[var(--ink)]">{isTbd ? `Any ${tier || "tier"} — TBD` : (person?.name || alloc.personId)}</div>
          <div className="eyebrow mt-0.5">{tier || "tier unknown"}</div>
          {alloc.removalRequested && (
            <span className="inline-block mt-1.5 px-2 py-0.5 border border-[#8a6d1a]/40 bg-[#FBF6EA] text-[#8a6d1a] text-[10px] font-display tracked-tight uppercase" data-testid={`removal-flag-${key}`}>
              Removal requested
            </span>
          )}
          <div className="text-[10px] font-mono-num text-[var(--soft-fg)] mt-1.5">
            by {alloc.proposedBy?.split("@")[0]}{alloc.proposedAt ? ` · ${new Date(alloc.proposedAt).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" })}` : ""}
          </div>
        </div>

        {/* Proposed 12-month strip (editable in approve-with-changes mode) */}
        <div className="flex-1 min-w-[320px]">
          <div className="eyebrow mb-1.5">{editing ? "Adjust, then approve" : "Proposed load"}</div>
          <div className="grid" style={{ gridTemplateColumns: `repeat(${m.length}, minmax(0,1fr))`, gap: 3 }}>
            {m.map((mo) => (
              <div key={mo} className="text-center">
                <div className="eyebrow text-[8px]">{fmtMonthShort(mo)}</div>
                {editing ? (
                  <input
                    value={vals[mo] ? Math.round(vals[mo] * 100) : ""}
                    onChange={(e) => setVals((v) => ({ ...v, [mo]: parseLoad(e.target.value) }))}
                    className="w-full border border-[var(--rule)] px-0.5 py-1 text-[10px] font-mono-num text-center outline-none focus:border-[var(--ink)]"
                    data-testid={`edit-${key}-${mo}`}
                  />
                ) : (
                  <div className={`py-1 text-[10px] font-mono-num border ${proposed[mo] ? "border-[var(--rule-strong)] bg-[var(--paper-soft)]" : "border-[var(--rule)]/40 text-[var(--rule-strong)]"}`}>
                    {proposed[mo] ? fmtPct(proposed[mo], 0) : "—"}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Month-by-month capacity strip: current · +proposed · resulting */}
          {isTbd && (
            <div className="mt-2.5">
              <div className="eyebrow mb-1">Assign a named {tier} <span className="normal-case">(least-loaded first)</span></div>
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="border border-[var(--rule-strong)] px-2 py-1 text-xs outline-none bg-white"
                data-testid={`assign-select-${key}`}
              >
                <option value="">choose person…</option>
                {tierPeople.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — avg {fmtPct(p.avgLoad, 0)} in window</option>
                ))}
              </select>
            </div>
          )}
          {strip.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`capacity-preview-${key}`}>
              {strip.map(({ mo, before, beforeDisp, delta, after, afterDisp, lv, over }) => (
                <span
                  key={mo}
                  className={`text-[10px] font-mono-num inline-flex items-center gap-1 border px-1.5 py-0.5 ${over ? "border-[#6B2F2A]/50 bg-[#FBF3F2] text-[#6B2F2A] font-medium" : "border-[var(--rule)] text-[var(--muted-fg)]"}`}
                  title={`${mo}: ${fmtPct(before,0)} booked now ${delta >= 0 ? "+" : "−"}${Math.round(Math.abs(delta)*100)}% proposed → ${fmtPct(after,0)} project load${lv > 0 ? ` · +${Math.round(lv*100)}% leave stacks on top → ${fmtPct(afterDisp,0)} total` : ""}${over ? " · over capacity" : ""}`}
                >
                  {over && <AlertTriangle size={9} />}
                  {fmtMonthShort(mo)} {fmtPct(beforeDisp, 0)}
                  <span className={delta >= 0 ? "text-[#4a5a7a]" : ""}>{delta >= 0 ? "+" : "−"}{Math.round(Math.abs(delta) * 100)}%</span>
                  → {fmtPct(afterDisp, 0)}
                  {lv > 0 && <Plane size={9} className="text-[var(--muted-fg)]" />}
                </span>
              ))}
            </div>
          )}
          {isTbd && !assignee && (
            <div className="text-[10px] font-mono-num text-[var(--soft-fg)] mt-1.5">
              TBD rows never hit the capacity matrix — pick a person to preview their before → after load.
            </div>
          )}
        </div>

        {/* Impact + actions */}
        <div className="min-w-[190px] text-right space-y-1.5">
          <div className="text-[11px] font-mono-num" data-testid={`cost-impact-${key}`}>
            Cost impact <span className="text-[var(--ink)]">{costImpact >= 0 ? "+" : "−"}{fmtMoney(Math.abs(costImpact))}</span>
          </div>
          <div className="text-[11px] font-mono-num" data-testid={`margin-context-${key}`}>
            Margin w/ proposal{" "}
            {marginWith == null ? <span className="text-[var(--soft-fg)]">—</span> : (
              <span style={{ color: marginColor(marginWith) }}>{(marginWith * 100).toFixed(1)}%</span>
            )}
          </div>
          <div className="flex justify-end gap-1.5 pt-1 flex-wrap">
            <button
              onClick={approve}
              disabled={busyKey === key}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--ink)] text-white text-[10px] font-display tracked-tight uppercase disabled:opacity-40"
              data-testid={`approve-${key}`}
            >
              <Check size={11} /> {editing ? "Approve changes" : alloc.removalRequested ? "Approve removal" : "Approve"}
            </button>
            {!alloc.removalRequested && (
              <button
                onClick={() => setEditing((e) => !e)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-[10px] font-display tracked-tight uppercase ${editing ? "border-[var(--ink)] bg-[var(--paper-soft)]" : "border-[var(--rule-strong)] hover:bg-[var(--paper-soft)]"}`}
                data-testid={`edit-toggle-${key}`}
              >
                <Pencil size={10} /> {editing ? "Cancel edit" : "With changes"}
              </button>
            )}
            <button
              onClick={() => setRejecting((r) => !r)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#6B2F2A]/40 text-[#6B2F2A] text-[10px] font-display tracked-tight uppercase hover:bg-[#FBF3F2]"
              data-testid={`reject-toggle-${key}`}
            >
              <X size={11} /> Reject
            </button>
          </div>
          {rejecting && (
            <div className="flex gap-1.5 pt-1.5 justify-end">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="note back to the principal…"
                className="border border-[var(--rule-strong)] px-2 py-1 text-[11px] outline-none w-48"
                data-testid={`reject-note-${key}`}
              />
              <button
                onClick={reject}
                disabled={busyKey === key}
                className="px-2.5 py-1 bg-[#6B2F2A] text-white text-[10px] font-display tracked-tight uppercase disabled:opacity-40"
                data-testid={`reject-confirm-${key}`}
              >
                Send
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
